import React from 'react';

/**
 * Шесть показателей платформы по артборду «Метрики платформы»
 * (docs/redesign/2026-09-04-bumaga/Newton - Админка.dc.html): подпись, крупное
 * число, снизу дельта к прошлому периоду и короткое пояснение.
 *
 * 🔴 Дельта считается ЧЕСТНО: тот же период, сдвинутый назад. Ради неё экран
 * делает второй запрос ряда — иначе пришлось бы либо рисовать выдуманный
 * процент, либо оставлять место пустым.
 */

const fmt = (n) => (typeof n === 'number' && Number.isFinite(n)
  ? Math.round(n).toLocaleString('ru-RU') : '—');

/** Сумма поля по корзинам ряда. */
export function sumBy(buckets, field) {
  return (buckets || []).reduce((acc, b) => acc + (b[field] || 0), 0);
}

/** Пик значения по корзинам — для того, что нельзя складывать (люди). */
export function peakBy(buckets, field) {
  return (buckets || []).reduce((acc, b) => Math.max(acc, b[field] || 0), 0);
}

/**
 * Дельта в процентах и её цвет. Рост ошибок — плохо, поэтому у них знак
 * читается наоборот: `goodDown`.
 */
export function delta(now, before, goodDown = false) {
  if (!before) return null;
  const pct = ((now - before) / before) * 100;
  const rounded = Math.abs(pct) >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10;
  const grew = pct > 0;
  const good = goodDown ? !grew : grew;
  return {
    text: `${grew ? '+' : '−'}${Math.abs(rounded).toLocaleString('ru-RU')}%`,
    good: Math.abs(pct) < 0.5 ? null : good,
  };
}

const Card = ({ label, value, change, sub }) => (
  <div className="flex flex-col gap-[10px] rounded-card bg-paper-card px-[22px] py-5">
    <div className="text-[12px] text-paper-ink-3">{label}</div>
    <div className="whitespace-nowrap font-display text-[34px] font-extrabold leading-none tracking-[-0.04em] text-paper-ink">
      {value}
    </div>
    <div className="flex items-baseline justify-between gap-2 text-[12px]">
      <span className={`whitespace-nowrap font-semibold ${
        change === null || change === undefined ? 'text-paper-hint'
          : change.good === null ? 'text-paper-ink-3'
            : change.good ? 'text-paper-success' : 'text-paper-accent'}`}>
        {change ? change.text : '—'}
      </span>
      <span className="min-w-0 truncate text-right text-paper-ink-3">{sub}</span>
    </div>
  </div>
);

export default function PlatformKpis({ current, previous, protocols, protocolsPrev, service }) {
  const tasks = sumBy(current, 'tasks');
  const tasksPrev = sumBy(previous, 'tasks');
  const minutes = sumBy(current, 'audio_seconds') / 60;
  const minutesPrev = sumBy(previous, 'audio_seconds') / 60;
  const errors = sumBy(current, 'error_count');
  const errorsPrev = sumBy(previous, 'error_count');
  const users = peakBy(current, 'active_users');
  const usersPrev = peakBy(previous, 'active_users');
  const newUsers = sumBy(current, 'new_users');
  const newUsersPrev = sumBy(previous, 'new_users');

  const errorRate = tasks ? (errors / tasks) * 100 : 0;
  const errorRatePrev = tasksPrev ? (errorsPrev / tasksPrev) * 100 : 0;
  const share = tasks ? Math.round((protocols / tasks) * 100) : 0;

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <Card label="Активные пользователи" value={fmt(users)}
            change={delta(users, usersPrev)} sub="пик за период" />
      <Card label="Новые пользователи" value={fmt(newUsers)}
            change={delta(newUsers, newUsersPrev)} sub="учётки за период" />
      <Card label="Задачи" value={fmt(tasks)}
            change={delta(tasks, tasksPrev)} sub={service || 'все сервисы'} />
      <Card label="Аудио, минут" value={fmt(minutes)}
            change={delta(minutes, minutesPrev)} sub={`≈ ${fmt(minutes / 60)} ч`} />
      <Card label="Протоколы" value={fmt(protocols)}
            change={delta(protocols, protocolsPrev)}
            sub={tasks ? `${share}% задач` : ''} />
      <Card label="Ошибки" value={`${errorRate.toFixed(1)}%`}
            change={delta(errorRate, errorRatePrev, true)} sub={`${fmt(errors)} задач`} />
    </div>
  );
}
